import { config } from '@iheartjane/config';
import type { ReservationMode } from '@iheartjane/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateLocalCartUuid } from '@jane/kiosk/data-access/lib/localCartUuid/localCartUuid';
import { api } from '@jane/shared/data-access';
import type { Cart, PendingCartProduct, Reservation } from '@jane/shared/types';

export const checkoutUrl = () => `${config.apiPath}/kiosk/carts/checkout`;

interface CheckoutRequest {
  aeropay_account_verified?: boolean;
  aeropay_bank_account_id?: string;
  birth_date?: string;
  cart_uuid: string;
  current_url: string;
  delivery_finished_at: string;
  delivery_started_at: string;
  j_device_id?: string;
  message?: string;
  name: string;
  payment_method?: string; //make more specific type?
  phone: string;
  products_attributes: PendingCartProduct[];
  reservation_mode: ReservationMode;
  store_id: number;
  user_consent?: Array<{
    checkout_agreement_version_id: number;
    consent: boolean;
  }>;
}

export type CreateCheckoutParams = Pick<
  CheckoutRequest,
  | 'aeropay_account_verified'
  | 'aeropay_bank_account_id'
  | 'birth_date'
  | 'cart_uuid'
  | 'current_url'
  | 'message'
  | 'j_device_id'
  | 'payment_method'
  | 'name'
  | 'phone'
  | 'store_id'
  | 'user_consent'
> & {
  products: Cart['products'];
};

export interface CheckoutResponse {
  message: string;
  reservation: Reservation;
}

const createCheckout = async ({
  products,
  ...checkoutParams
}: CreateCheckoutParams) => {
  const nowSeconds = Math.floor(Date.now() / 1000).toString();
  const requestBody: CheckoutRequest = {
    delivery_finished_at: nowSeconds,
    delivery_started_at: nowSeconds,
    products_attributes: products.map((product) => ({
      ...product,
      current_special_id: product.special_id,
    })),
    reservation_mode: 'kiosk',
    ...checkoutParams,
  };
  return api.post<CheckoutResponse>(checkoutUrl(), { cart: requestBody });
};

type SubmitOrderParams = {
  onError: (error: unknown, params: CreateCheckoutParams) => unknown;
  onSuccess: (
    response: CheckoutResponse,
    params: CreateCheckoutParams
  ) => unknown;
};
export const useSubmitOrder = ({ onError, onSuccess }: SubmitOrderParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCheckout,
    onError,
    onSuccess: async (data, params) => {
      updateLocalCartUuid(null);
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      onSuccess(data, params);
    },
  });
};
